<template>
  <div class="space-y-4">
    <div>
      <label class="block text-sm font-medium text-discord-light-100 mb-2">
        {{ t('configuracoes.tagColorLabel') }}
      </label>
      <div class="grid grid-cols-10 gap-3">
        <button
          v-for="color in colors"
          :key="color.value"
          @click="selectColor(color.value)"
          type="button"
          class="w-10 h-10 rounded-xl border-2 transition-all duration-200 flex items-center justify-center group hover:scale-105"
          :class="selectedColor === color.value ? 'border-discord-blurple scale-110 shadow-lg shadow-discord-blurple/30' : 'border-discord-dark-200 hover:border-discord-dark-100 hover:shadow-md'"
          :style="{ backgroundColor: color.value }"
          :disabled="disabled"
          :title="color.label"
        >
          <svg 
            v-if="selectedColor === color.value"
            class="w-5 h-5 text-white drop-shadow-sm" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
          </svg>
          <div 
            v-else
            class="w-3 h-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            style="background-color: rgba(255, 255, 255, 0.3)"
          ></div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  modelValue: {
    type: String,
    default: '#5865F2'
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const selectedColor = ref(props.modelValue)

// Mesmas cores do workspace + variações de cinza e branco
const colorDefs = [
  { nameKey: 'discordBlue', value: '#5865F2' },
  { nameKey: 'discordGreen', value: '#57F287' },
  { nameKey: 'discordYellow', value: '#FEE75C' },
  { nameKey: 'discordFuchsia', value: '#EB459E' },
  { nameKey: 'discordRed', value: '#ED4245' },
  { nameKey: 'discordOrange', value: '#F57C00' },
  { nameKey: 'materialPurple', value: '#9C27B0' },
  { nameKey: 'materialPink', value: '#E91E63' },
  { nameKey: 'lightBlue', value: '#03A9F4' },
  { nameKey: 'lightGreen', value: '#4CAF50' },
  { nameKey: 'mediumGray', value: '#607D8B' },
  { nameKey: 'brown', value: '#795548' },
  { nameKey: 'darkBlue', value: '#1976D2' },
  { nameKey: 'darkGreen', value: '#388E3C' },
  { nameKey: 'darkRed', value: '#D32F2F' },
  { nameKey: 'teal', value: '#009688' },
  { nameKey: 'indigo', value: '#3F51B5' },
  { nameKey: 'materialCyan', value: '#00BCD4' },
  { nameKey: 'materialLime', value: '#CDDC39' },
  { nameKey: 'amber', value: '#FFC107' },
  { nameKey: 'deepOrange', value: '#FF5722' },
  { nameKey: 'deepPurple', value: '#673AB7' },
  { nameKey: 'materialLightGreen', value: '#8BC34A' },
  { nameKey: 'materialYellow', value: '#FFEB3B' },
  { nameKey: 'materialRed', value: '#F44336' },
  { nameKey: 'materialBlue', value: '#2196F3' },
  { nameKey: 'white', value: '#FFFFFF' },
  { nameKey: 'grayVeryLight', value: '#F5F5F5' },
  { nameKey: 'grayLightAlt', value: '#E0E0E0' },
  { nameKey: 'grayMediumLight', value: '#BDBDBD' },
  { nameKey: 'grayMedium', value: '#9E9E9E' },
  { nameKey: 'grayMediumDark', value: '#757575' },
  { nameKey: 'grayDarkAlt', value: '#424242' },
  { nameKey: 'black', value: '#000000' }
]

const colors = computed(() =>
  colorDefs.map((c) => ({
    ...c,
    label: t(`colors.names.${c.nameKey}`)
  }))
)

const selectColor = (colorValue) => {
  if (props.disabled) return
  
  selectedColor.value = colorValue
  emit('update:modelValue', colorValue)
}

// Sincronizar com prop externa
watch(() => props.modelValue, (newValue) => {
  selectedColor.value = newValue
}, { immediate: true })
</script>